/**
 * Katalogda olmayan otel (28.10.2026): otel arama kutusunda bulunamayan oteli satışçı kendisi ekler. Form "Oteller" kartında,
 * eklenenlerin listesi "Sonuçlar" kartında durur (satır = seçenek, onay kutusu teklife ekler). Canlı fiyat çekilmez; tutar
 * satışçının yazdığıdır. Fotoğraf yoktur. Ekran mevcut tasarım sistemi bileşenleriyle çizilir, ham tarih alanı yoktur
 * (konaklama tarihleri bölümdeki tarih seçicisinden gelir, gece sayısı oradan hesaplanır).
 */
import { useId, useState } from 'react';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import { Ikon } from './Ikon';
import { manuelDegerlendir, manuelGeceCoz, paraYaz, tutarCoz, tutarYaz } from './mantik';
import type { ManuelOtel, ParaBirimi } from './tipler';

const PARALAR: readonly ParaBirimi[] = ['USD', 'AED', 'EUR'];

export function ManuelOtelFormu({ baslangic, gece, kaydet, vazgec, onek }: { baslangic: ManuelOtel; gece: number | null; kaydet: (m: ManuelOtel) => void; vazgec: () => void; onek: string }) {
  const kimlik = useId();
  const [m, setM] = useState<ManuelOtel>(baslangic);
  const [denendi, setDenendi] = useState(false);
  const d = manuelDegerlendir(m, gece);
  // Gecelik tutarda tarih eksikliği kaydı engellemez (satır "tarih seçilince hesaplanır" der, kapı kapalı kalır).
  const engel = d.hata !== null && d.hata !== 'TARIH';
  const alan = (k: keyof ManuelOtel, deger: string): void => setM((x) => ({ ...x, [k]: deger }));
  const hataMetni = denendi && engel && d.hata !== null ? M.manuel.hata[d.hata] : null;
  const tutar = tutarCoz(m.tutar);
  return (
    <form
      className="ht-manuel-form"
      noValidate
      aria-labelledby={`${kimlik}-baslik`}
      data-testid={`${onek}-manuel-form`}
      onSubmit={(e) => { e.preventDefault(); setDenendi(true); if (!engel) kaydet({ ...m, ad: m.ad.trim(), sehir: m.sehir.trim(), odaAdi: m.odaAdi.trim(), iptalMetni: m.iptalMetni.trim(), ekNot: m.ekNot.trim() }); }}
    >
      <h3 id={`${kimlik}-baslik`}>{M.manuel.ekle}</h3>
      <div className="ht-manuel-izgara">
        <div className="ht-alan">
          <label htmlFor={`${kimlik}-ad`}>{M.manuel.ad}</label>
          <input id={`${kimlik}-ad`} value={m.ad} maxLength={120} autoComplete="off" data-testid={`${onek}-manuel-ad`} aria-invalid={denendi && d.hata === 'AD' ? 'true' : undefined} onChange={(e) => alan('ad', e.target.value)} />
        </div>
        <div className="ht-alan">
          <label htmlFor={`${kimlik}-sehir`}>{M.manuel.sehir}</label>
          <input id={`${kimlik}-sehir`} value={m.sehir} maxLength={60} autoComplete="off" data-testid={`${onek}-manuel-sehir`} aria-invalid={denendi && d.hata === 'SEHIR' ? 'true' : undefined} onChange={(e) => alan('sehir', e.target.value)} />
        </div>
        <div className="ht-alan ht-manuel-genis">
          <label htmlFor={`${kimlik}-oda`}>{M.manuel.oda}</label>
          <input id={`${kimlik}-oda`} value={m.odaAdi} maxLength={160} autoComplete="off" data-testid={`${onek}-manuel-oda`} aria-invalid={denendi && d.hata === 'ODA' ? 'true' : undefined} onChange={(e) => alan('odaAdi', e.target.value)} />
        </div>
        <div className="ht-alan" role="radiogroup" aria-label={M.manuel.tutarTuru}>
          <span className="ht-etiket">{M.manuel.tutarTuru}</span>
          <label className="ht-arac-secenek"><input type="radio" name={`${kimlik}-tur`} checked={m.tutarTuru === 'TOPLAM'} onChange={() => setM((x) => ({ ...x, tutarTuru: 'TOPLAM' }))} data-testid={`${onek}-manuel-toplam`} /><span>{M.manuel.toplam}</span></label>
          <label className="ht-arac-secenek"><input type="radio" name={`${kimlik}-tur`} checked={m.tutarTuru === 'GECELIK'} onChange={() => setM((x) => ({ ...x, tutarTuru: 'GECELIK' }))} data-testid={`${onek}-manuel-gecelik`} /><span>{M.manuel.gecelik}</span></label>
        </div>
        <div className="ht-alan">
          <label htmlFor={`${kimlik}-tutar`}>{M.manuel.tutar}</label>
          <input id={`${kimlik}-tutar`} value={m.tutar} inputMode="decimal" placeholder={M.manuel.tutarIpucu} autoComplete="off" data-testid={`${onek}-manuel-tutar`} aria-invalid={denendi && d.hata === 'TUTAR' ? 'true' : undefined} onChange={(e) => alan('tutar', e.target.value)} />
          {tutar !== null ? <span className="ht-ipucu">{paraYaz(tutar, m.paraBirimi)}{m.tutarTuru === 'GECELIK' && d.gece !== null ? ` x ${String(d.gece)} gece = ${paraYaz(tutar * d.gece, m.paraBirimi)}` : ''}</span> : null}
        </div>
        <div className="ht-alan">
          <label htmlFor={`${kimlik}-gece`}>{M.manuel.gece}</label>
          <input id={`${kimlik}-gece`} type="number" inputMode="numeric" min={1} max={60} step={1} value={m.gece} autoComplete="off" aria-invalid={denendi && d.hata === 'GECE' ? 'true' : undefined} data-testid={`${onek}-manuel-gece`} onChange={(e) => alan('gece', e.target.value)} />
          <span className="ht-ipucu">{M.manuel.geceIpucu(gece === null ? M.manuel.geceYok : M.manuel.geceTarihten(gece))}</span>
        </div>
        <div className="ht-alan">
          <label htmlFor={`${kimlik}-para`}>{M.manuel.para}</label>
          <select id={`${kimlik}-para`} value={m.paraBirimi} data-testid={`${onek}-manuel-para`} onChange={(e) => setM((x) => ({ ...x, paraBirimi: e.target.value as ParaBirimi }))}>
            {PARALAR.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="ht-alan ht-manuel-genis">
          <label htmlFor={`${kimlik}-iptal`}>{M.manuel.iptal}</label>
          <textarea id={`${kimlik}-iptal`} rows={2} maxLength={300} value={m.iptalMetni} placeholder={M.manuel.iptalIpucu} data-testid={`${onek}-manuel-iptal`} onChange={(e) => alan('iptalMetni', e.target.value)} />
        </div>
        <div className="ht-alan ht-manuel-genis">
          <label htmlFor={`${kimlik}-not`}>{M.manuel.ekNot}</label>
          <textarea id={`${kimlik}-not`} rows={2} maxLength={600} value={m.ekNot} data-testid={`${onek}-manuel-not`} onChange={(e) => alan('ekNot', e.target.value)} />
        </div>
      </div>
      {hataMetni !== null ? <p className="ht-hata-yazi" role="alert" data-testid={`${onek}-manuel-hata`}>{hataMetni}</p> : null}
      <div className="ht-manuel-eylem">
        <button type="submit" className="ht-btn ht-btn-p" data-testid={`${onek}-manuel-kaydet`}><Ikon ad="tik" kucuk />{M.manuel.kaydet}</button>
        <button type="button" className="ht-btn" onClick={vazgec} data-testid={`${onek}-manuel-vazgec`}>{M.manuel.vazgec}</button>
      </div>
    </form>
  );
}

export function ManuelOtelListesi({
  oteller,
  isaretli,
  gece,
  tekSecim,
  salt,
  grupAdi,
  onek,
  isaretle,
  duzenle,
  kaldir,
}: {
  oteller: readonly ManuelOtel[];
  isaretli: ReadonlySet<string>;
  gece: number | null;
  tekSecim: boolean;
  salt: boolean;
  grupAdi: string;
  onek: string;
  isaretle: (id: string, deger: boolean) => void;
  duzenle: (id: string) => void;
  kaldir: (id: string) => void;
}) {
  if (oteller.length === 0) return null;
  return (
    <div className="ht-manuel-liste" data-testid={`${onek}-manuel-liste`}>
      <h3>{M.manuel.baslik}</h3>
      <div className="ht-tablokap">
        <table className="ht-tablo ht-manuel-tablo">
          <caption className="ht-sr">{M.manuel.baslik}</caption>
          <thead>
            <tr><th scope="col" className="ht-k-onay"><span className="ht-sr">{M.kolonOnay}</span></th><th scope="col">{M.kolonOtel}</th><th scope="col">{M.manuel.oda}</th><th scope="col" className="ht-sag">{M.manuel.tutar}</th><th scope="col"><span className="ht-sr">{M.kolonYenile}</span></th></tr>
          </thead>
          <tbody>
            {oteller.map((m) => {
              const d = manuelDegerlendir(m, gece);
              const secili = isaretli.has(m.id);
              const secilebilir = d.hata === null && d.toplamMinor !== null && !salt;
              return (
                <tr key={m.id} className={secili ? 'ht-satir-secili' : undefined} data-testid={`${onek}-manuel-${m.id}`}>
                  <td className="ht-k-onay">
                    <input
                      type={tekSecim ? 'radio' : 'checkbox'}
                      name={tekSecim ? grupAdi : undefined}
                      className={`ht-cb${tekSecim ? ' ht-rb' : ''}`}
                      checked={secili}
                      disabled={!secilebilir && !secili}
                      aria-label={M.manuel.teklife(m.ad)}
                      data-testid={`${onek}-manuel-onay-${m.id}`}
                      onChange={(e) => isaretle(m.id, e.target.checked)}
                    />
                  </td>
                  <th scope="row">
                    <div className="ht-otel-metin">
                      <span className="ht-otel-ad">{m.ad}</span>
                      <span className="ht-otel-meta"><span>{m.sehir}</span><span className="ht-et">{M.manuel.etiket}</span></span>
                      {d.hata !== null && d.hata !== 'TARIH' ? <span className="ht-hata-yazi">{M.manuel.hata[d.hata]}</span> : null}
                    </div>
                  </th>
                  <td><span className="ht-kombinasyon-ad">{m.odaAdi}</span></td>
                  <td className="ht-sag">
                    {d.toplamMinor === null ? <span className="ht-ipucu">{M.manuel.fiyatYok}</span> : <span className="ht-toplam" data-testid={`${onek}-manuel-toplam-${m.id}`}>{tutarYaz(d.toplamMinor)} <span className="ht-para">{m.paraBirimi}</span></span>}
                    {m.tutarTuru === 'GECELIK' && d.gecelikMinor !== null && d.gece !== null ? <small className="ht-ipucu">{M.manuel.gecelikMetni(paraYaz(d.gecelikMinor, m.paraBirimi), d.gece)}</small> : null}
                    {manuelGeceCoz(m.gece).deger !== null ? <small className="ht-ipucu" data-testid={`${onek}-manuel-gece-bilgi-${m.id}`}>{M.manuel.geceElle(manuelGeceCoz(m.gece).deger as number)}</small> : null}
                  </td>
                  <td className="ht-k-yenile">
                    {salt ? null : (
                      <span className="ht-manuel-islem">
                        <button type="button" className="ht-ikon-dugme" onClick={() => duzenle(m.id)} aria-label={M.manuel.duzenle(m.ad)} data-testid={`${onek}-manuel-duzenle-${m.id}`}><Ikon ad="duzenle" /></button>
                        <button type="button" className="ht-ikon-dugme" onClick={() => kaldir(m.id)} aria-label={M.manuel.kaldir(m.ad)} data-testid={`${onek}-manuel-kaldir-${m.id}`}><Ikon ad="kapat" /></button>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

